import React from 'react';
import { Editor } from 'slate';
import { useSlate, useSelected } from 'slate-react';
import {
  type AudioElement,
  type ComponentProps,
  type EditorPlugin,
} from '../../../types/aha-editor';
import MediaCommon from '../MediaCommon';
import {
  withImagePosition,
  ToolPositionInline,
  ToolPositionBlock,
} from '../tools/ToolPosition';
import ToolDelete from '../../../tools/ToolDelete';
import ToolEditAlt from '../tools/ToolEditAlt';
import SelectionRectangle from '../../../components/SelectionRectangle/SelectionRectangle';
import { isAudioElement } from '../../../types/guards';
import MediaLoader from './MediaLoader';

const IMAGE_PLACEHOLDER = `<svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="image" class="svg-inline--fa fa-image" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="#dddddd" d="M152 120c-26.51 0-48 21.49-48 48s21.49 48 48 48s48-21.49 48-48S178.5 120 152 120zM447.1 32h-384C28.65 32-.0091 60.65-.0091 96v320c0 35.35 28.65 64 63.1 64h384c35.35 0 64-28.65 64-64V96C511.1 60.65 483.3 32 447.1 32zM463.1 409.3l-136.8-185.9C323.8 218.8 318.1 216 312 216c-6.113 0-11.82 2.768-15.21 7.379l-106.6 144.1l-37.09-46.1c-3.441-4.279-8.934-6.809-14.77-6.809c-5.842 0-11.33 2.529-14.78 6.809l-75.52 93.81c0-.0293 0 .0293 0 0L47.99 96c0-8.822 7.178-16 16-16h384c8.822 0 16 7.178 16 16V409.3z"></path></svg>`;

// Render an audio element.
const Audio: React.FC<ComponentProps<AudioElement>> = ({
  element,
  children,
}) => {
  const selected = useSelected();
  const editor = useSlate();

  if (!isAudioElement(element)) return null;

  const { media_src: src, media_alt: alt } = element;

  let imageUrl: string | undefined;
  if (MediaCommon.isTemporaryDataUrl(src)) {
    imageUrl = MediaCommon.getTemporaryData(src);
    if (!imageUrl) {
      // The image is being uploaded by a collaborator, or the upload failed
      // and our page was reloaded. Display a placeholder image instead.
      imageUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
        IMAGE_PLACEHOLDER
      )}`;
    }
  } else {
    imageUrl = src;
  }

  // The <audio> element captures keyboard events, so we need to override it
  // to catch deletes.
  const handleDelete = (event: React.KeyboardEvent) => {
    const key = event.key;
    if (key === 'Backspace' || key === 'Delete') {
      event.preventDefault();
      Editor.deleteForward(editor, { unit: 'character' });
    }
  };

  return (
    <>
      <SelectionRectangle>
        <audio
          controls
          src={imageUrl}
          onKeyDown={handleDelete}
          aria-label={alt}
        />
        <MediaLoader element={element} />
        {!selected && (
          /*
           * Overlay element to it possible to select this element by clicking.
           * This will mask the controls until the element is selected.
           */
          <span
            style={{
              display: 'inline-block',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
          />
        )}
        {children}
      </SelectionRectangle>
    </>
  );
};

const AudioComponentPlugin: EditorPlugin = {
  name: 'Audio component plugin',
  component: {
    type: 'audio',
    component: withImagePosition(Audio),
    toolbarItems: [
      [ToolPositionBlock, ToolPositionInline],
      [ToolEditAlt],
      [ToolDelete],
    ],
  },
};

export default AudioComponentPlugin;
